import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { adminApi, apiError, formatDateTime, REG_STATUS } from "@/admin/adminApi";
import { Download, StickyNote } from "lucide-react";
import { PageHead } from "@/admin/AdminLayout";
import RegistrationSwitch from "@/admin/components/RegistrationSwitch";
import NoteDialog from "@/admin/components/NoteDialog";
import SeatFreedAlerts from "@/admin/components/SeatFreedAlerts";
import { btnGhost, card, fieldInput, fieldLabel } from "@/admin/ui";

function Filters({ programs, sessions, filters, setFilters }) {
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value, ...(key === "program_id" ? { session_id: "" } : {}) }));
  const visibleSessions = sessions.filter((s) => !filters.program_id || s.program_id === filters.program_id);
  return (
    <div className="grid md:grid-cols-3 gap-4" data-testid="registrations-filters">
      <div>
        <label className={fieldLabel} htmlFor="rf-program">Program</label>
        <select id="rf-program" value={filters.program_id} onChange={set("program_id")} data-testid="registrations-filter-program" className={fieldInput}>
          <option value="">Minden program</option>
          {programs.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>
      <div>
        <label className={fieldLabel} htmlFor="rf-session">Időpont</label>
        <select id="rf-session" value={filters.session_id} onChange={set("session_id")} data-testid="registrations-filter-session" className={fieldInput}>
          <option value="">Minden időpont</option>
          {visibleSessions.map((s) => <option key={s.id} value={s.id}>{formatDateTime(s.starts_at)}</option>)}
        </select>
      </div>
      <div>
        <label className={fieldLabel} htmlFor="rf-status">Státusz</label>
        <select id="rf-status" value={filters.status} onChange={set("status")} data-testid="registrations-filter-status" className={fieldInput}>
          <option value="">Minden státusz</option>
          {Object.entries(REG_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
    </div>
  );
}

function RegistrationsTable({ rows, programs, sessions, onStatus, onNote }) {
  const progName = useMemo(() => Object.fromEntries(programs.map((p) => [p.id, p.title])), [programs]);
  const sessDate = useMemo(() => Object.fromEntries(sessions.map((s) => [s.id, formatDateTime(s.starts_at)])), [sessions]);
  if (rows.length === 0) {
    return <p data-testid="registrations-empty" className="font-body text-[14.5px] text-graphite-soft py-10 text-center">Még nincs beérkezett jelentkezés.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left font-body text-[14px]" data-testid="registrations-table">
        <thead>
          <tr className="text-[10.5px] tracking-[0.22em] uppercase text-muted-warm border-b border-hairline">
            <th className="py-3 pr-4 font-normal">Név és e-mail</th>
            <th className="py-3 pr-4 font-normal">Program és időpont</th>
            <th className="py-3 pr-4 font-normal">Üzenet</th>
            <th className="py-3 pr-4 font-normal">Beérkezett</th>
            <th className="py-3 font-normal">Státusz</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-hairline align-top" data-testid={`registration-row-${r.id}`}>
              <td className="py-3.5 pr-4"><p className="text-[11px] tracking-[0.12em] text-muted-warm">{r.id}</p><p>{r.name}</p><a href={`mailto:${r.email}`} className="text-[13px] text-gold">{r.email}</a></td>
              <td className="py-3.5 pr-4"><p>{progName[r.program_id] || "—"}</p><p className="text-[12.5px] text-graphite-soft">{sessDate[r.session_id] || ""}</p></td>
              <td className="py-3.5 pr-4 max-w-[260px] text-[13px] text-graphite-soft whitespace-pre-line">
                {r.message || "—"}
                <button type="button" onClick={() => onNote(r)} data-testid={`registration-note-${r.id}`} className="mt-2 flex items-center gap-1.5 text-[12px] text-gold hover:text-graphite text-left">
                  <StickyNote className="w-3.5 h-3.5 flex-shrink-0" /> {r.note ? `Jegyzet: ${r.note.length > 40 ? `${r.note.slice(0, 40)}…` : r.note}` : "Jegyzet hozzáadása"}
                </button>
              </td>
              <td className="py-3.5 pr-4 text-[13px] whitespace-nowrap">{new Date(r.created_at).toLocaleDateString("hu-HU")}</td>
              <td className="py-3.5">
                {r.status === "waitlist" && r.waitlist_position && (
                  <p data-testid={`registration-waitlist-pos-${r.id}`} className="mb-1.5 text-[11px] tracking-[0.16em] uppercase text-gold">Várólista #{r.waitlist_position}</p>
                )}
                <select value={r.status} onChange={(e) => onStatus(r, e.target.value)} data-testid={`registration-status-${r.id}`} className={`${fieldInput} !py-1.5 !text-[13px]`}>
                  {Object.entries(REG_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminRegistrations() {
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [rows, setRows] = useState(null);
  const location = useLocation();
  const [filters, setFilters] = useState(location.state?.filters || { program_id: "", session_id: "", status: "" });

  const loadSessions = () => adminApi.get("/admin/sessions").then((s) => setSessions(s.data)).catch((e) => toast.error(apiError(e)));
  const loadRows = (f = filters) => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    return adminApi.get("/admin/registrations", { params }).then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  };

  useEffect(() => {
    adminApi.get("/admin/programs").then((p) => setPrograms(p.data)).catch((e) => toast.error(apiError(e)));
    loadSessions();
  }, []);

  useEffect(() => {
    loadRows(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const [noteFor, setNoteFor] = useState(null);

  const saveNote = async (reg, note) => {
    try {
      const { data } = await adminApi.patch(`/admin/registrations/${reg.id}/note`, { note });
      setRows((list) => list.map((x) => (x.id === data.id ? data : x)));
      setNoteFor(null);
      toast.success("A jegyzet elmentve.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const exportQuery = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString();
  const exportHref = `${process.env.REACT_APP_BACKEND_URL}/api/admin/registrations-export${exportQuery ? `?${exportQuery}` : ""}`;

  const changeStatus = async (reg, status) => {
    try {
      await adminApi.patch(`/admin/registrations/${reg.id}`, { status });
      await Promise.all([loadRows(), loadSessions()]);
      toast.success("A státusz elmentve.");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="admin-registrations-page">
      <PageHead eyebrow="Jelentkezők" title="Jelentkezők">
        <a href={exportHref} data-testid="registrations-export-btn" className={btnGhost}>
          <Download className="w-3.5 h-3.5" /> Táblázat letöltése
        </a>
      </PageHead>
      <RegistrationSwitch />
      <div className="mt-6">
        <SeatFreedAlerts
          sessions={sessions.filter((s) => s.seat_freed_alert)}
          programs={Object.fromEntries(programs.map((p) => [p.id, p.title]))}
          onPick={(s) => setFilters({ program_id: s.program_id, session_id: s.id, status: "waitlist" })}
        />
      </div>
      <section className={`${card} mt-8 px-6 md:px-8 py-7`}>
        <Filters programs={programs} sessions={sessions} filters={filters} setFilters={setFilters} />
        <div className="mt-6">
          {rows === null ? <p className="font-body text-graphite-soft">Betöltés…</p> : (
            <RegistrationsTable rows={rows} programs={programs} sessions={sessions} onStatus={changeStatus} onNote={setNoteFor} />
          )}
        </div>
      </section>
      <NoteDialog reg={noteFor} onClose={() => setNoteFor(null)} onSave={saveNote} />
    </div>
  );
}
